import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import { formatDateTime } from 'services/utils/dateTimeUtils';
import { useSettings } from 'services/settings/SettingsContext';
import { useSocketMessage } from 'services/api/updatesSocket';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faXmark, faAt } from '@fortawesome/free-solid-svg-icons';
import { useMentions, MentionType } from './useMentions';
import '../../style/scss/navigation.scss';

export interface EnvironmentNotificationType {
    id: number;
    type: string;
    data: Record<string, string>;
    link?: string | null;
    created: string;
};

// A row of the panel, an environment notification or a mention of the user
type PanelItem =
    | { kind: 'notification'; created: string; notification: EnvironmentNotificationType }
    | { kind: 'mention'; created: string; mention: MentionType };

// Bell icon in the navbar with a badge and a panel showing the environment notifications and mentions
const NotificationBell: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { handleLogout } = useAuthContext();
    const { userLocale, userTimezone } = useSettings();
    const [notifications, setNotifications] = useState<EnvironmentNotificationType[]>([]);
    const [panelVisible, setPanelVisible] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const handleLogoutRef = useRef(handleLogout);
    const { mentions, unreadCount, openMention, dismissMention } = useMentions();
    const { message } = useSocketMessage(['environment_notifications_updated']);

    // Keep the logout handler in a ref so the fetch callback stays stable
    handleLogoutRef.current = handleLogout;

    // Fetch the open notifications of the environment
    const fetchNotifications = useCallback(async () => {
        try {
            const response = await fetchData({ apiUrl: 'get_environment_notification_list', handleLogout: handleLogoutRef.current });
            setNotifications(response?.results ?? []);
        } catch (error) {
            console.error('Error fetching notifications', error);
        }
    }, []);

    // Fetch the notifications once on mount
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // Refresh the notifications when the backend reports a change over the websocket
    useEffect(() => {
        if (message) fetchNotifications();
    }, [message, fetchNotifications]);

    // Close the panel when clicking outside it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (panelVisible && panelRef.current && !panelRef.current.contains(event.target as Node)) {
                setPanelVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [panelVisible]);

    // Dismiss a notification so it disappears from the panel
    const dismissNotification = async (event: React.MouseEvent, notificationId: number) => {
        event.stopPropagation();
        try {
            await saveData({
                apiUrl: `dismiss_environment_notification/${notificationId}`,
                method: 'post',
                data: {}
            });
            setNotifications(current => current.filter(notification => notification.id !== notificationId));
        } catch (error) {
            console.error('Error dismissing notification', error);
        }
    };

    // Navigate to the link of a notification
    const openNotification = (notification: EnvironmentNotificationType) => {
        if (notification.link) {
            setPanelVisible(false);
            history.push(notification.link);
        }
    };

    // Open the note of a mention and close the panel
    const handleOpenMention = (mention: MentionType) => {
        setPanelVisible(false);
        openMention(mention);
    };

    // Combine the notifications and mentions, newest first
    const panelItems: PanelItem[] = [
        ...notifications.map(notification => ({ kind: 'notification' as const, created: notification.created, notification })),
        ...mentions.map(mention => ({ kind: 'mention' as const, created: mention.created, mention })),
    ].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

    // The badge counts the open notifications and the unread mentions
    const badgeCount = notifications.length + unreadCount;

    return (
        <div className='notification-bell' ref={panelRef}>

            {/* The bell icon with the open notifications badge */}
            <div className='bell-icon' onClick={() => setPanelVisible(!panelVisible)}>
                <FontAwesomeIcon icon={faBell} />
                {badgeCount > 0 && (
                    <span className='notification-badge'>{badgeCount}</span>
                )}
            </div>

            {/* The notification panel */}
            {panelVisible && (
                <div className='notification-panel'>
                    <div className='notification-panel-header'>
                        {t('notifications.general.panel_title')}
                    </div>
                    {panelItems.length === 0 ? (
                        <div className='notification-empty'>
                            {t('notifications.general.no_notifications_label')}
                        </div>
                    ) : (
                        panelItems.map(item => item.kind === 'mention' ? (
                            <div
                                key={`mention-${item.mention.id}`}
                                className={`notification-item mention clickable ${item.mention.read ? '' : 'unread'}`}
                                onClick={() => handleOpenMention(item.mention)}
                            >
                                <FontAwesomeIcon icon={faAt} className='notification-icon' />
                                <div className='notification-content'>
                                    <span className='notification-title'>
                                        {item.mention.mentioned_by_name
                                            ? t('notifications.mention.title', { name: item.mention.mentioned_by_name })
                                            : t('notifications.mention.title_unknown')}
                                    </span>
                                    <span className='notification-page'>
                                        {item.mention.page_type ? t(`${item.mention.page_type}.general.object_name.singular`) : t(`notifications.mention.source_${item.mention.source}`)} {item.mention.page_name}
                                    </span>
                                    <span className='notification-message'>
                                        {item.mention.text}
                                    </span>
                                    <span className='notification-date'>
                                        {formatDateTime(item.mention.created, userLocale, userTimezone)}
                                    </span>
                                </div>
                                <div
                                    className='notification-dismiss'
                                    title={t('notifications.general.dismiss_label')}
                                    onClick={(event) => { event.stopPropagation(); dismissMention(item.mention); }}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </div>
                            </div>
                        ) : (
                            <div
                                key={`notification-${item.notification.id}`}
                                className={`notification-item ${item.notification.link ? 'clickable' : ''}`}
                                onClick={() => openNotification(item.notification)}
                            >
                                <div className='notification-content'>
                                    <span className='notification-title'>
                                        {t(`notifications.${item.notification.type}.title`)}
                                    </span>
                                    <span className='notification-message'>
                                        {t(`notifications.${item.notification.type}.message`, item.notification.data)}
                                    </span>
                                    <span className='notification-date'>
                                        {formatDateTime(item.notification.created, userLocale, userTimezone)}
                                    </span>
                                </div>
                                <div
                                    className='notification-dismiss'
                                    title={t('notifications.general.dismiss_label')}
                                    onClick={(event) => dismissNotification(event, item.notification.id)}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
